import { MenuItem, IconButton, Divider, Popover } from "@mui/material";
import { useState } from "react";
import { styled } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import EditIcon from "@mui/icons-material/Edit";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import CustomConfirmDialog from "@/components/custom/custom-confirm-dialog";
import {
  deleteRoutingProfile,
  toggleRoutingProfileStatus,
  toggleRoutingProfileStrategy,
} from "@/services/tech.service";

import { useDispatch } from "react-redux";
import { showToast } from "@/redux/actions/toast-actions";
import RoutingProfileSheet from "./routing-profile-sheet";
import UpdateRoutingProfileDialog from "./update-routing-profile";

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== "danger",
})(({ theme, danger }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  fontSize: "0.875rem",
  padding: theme.spacing(1, 2),
  color: danger ? theme.palette.error.main : "inherit",
  "&:hover": {
    backgroundColor: danger
      ? `${theme.palette.error.main}10`
      : theme.palette.action.hover,
  },
}));

const PopoverContent = styled("div")(({ theme }) => ({
  minWidth: 180,
  padding: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
}));

const RouteProfilePopover = ({ profile, onView }) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toggleOpen, setToggleOpen] = useState(false);
  const [strategyOpen, setStrategyOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const isOpen = Boolean(anchorEl);

  const isActive = profile?.isActive;
  const currentStrategy = profile?.selectionStrategy;
  const nextStrategy = currentStrategy === "ALL" ? "ONE" : "ALL";

  const handleView = () => {
    handleClose();
    onView?.(profile);
    setSheetOpen(true);
  };

  const deleteMutation = useMutation({
    mutationFn: () => deleteRoutingProfile(profile?.id),
    onSuccess: (data) => {
      dispatch(showToast(data?.message ?? "Deleted successfully", "success"));
      queryClient.invalidateQueries({ queryKey: ["routing-profiles"] });
    },
    onError: (err) => {
      dispatch(showToast(err?.message ?? "Delete failed", "error"));
    },
  });

  const toggleMutation = useMutation({
    mutationFn: () =>
      toggleRoutingProfileStatus({
        id: profile?.id,
        isActive: !isActive,
      }),
    onSuccess: (data) => {
      dispatch(
        showToast(
          data?.message ??
            `Profile ${!isActive ? "activated" : "deactivated"} successfully`,
          "success",
        ),
      );
      queryClient.invalidateQueries({ queryKey: ["routing-profiles"] });
    },
    onError: (err) => {
      dispatch(showToast(err?.message ?? "Failed to update status", "error"));
    },
  });

  const strategyMutation = useMutation({
    mutationFn: () =>
      toggleRoutingProfileStrategy({
        id: profile?.id,
        strategy: nextStrategy,
      }),
    onSuccess: (data) => {
      dispatch(
        showToast(
          data?.message ?? `Strategy updated to ${nextStrategy} successfully`,
          "success",
        ),
      );
      queryClient.invalidateQueries({ queryKey: ["routing-profiles"] });
    },
    onError: (err) => {
      dispatch(showToast(err?.message ?? "Failed to update strategy", "error"));
    },
  });

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <IconButton size="small" onClick={handleOpen}>
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <PopoverContent>
          <StyledMenuItem onClick={handleView}>
            <VisibilityIcon fontSize="small" />
            View Profile
          </StyledMenuItem>

          <StyledMenuItem
            onClick={() => {
              handleClose();
              setUpdateOpen(true);
            }}
          >
            <EditIcon fontSize="small" />
            Edit Profile
          </StyledMenuItem>

          <StyledMenuItem
            disabled={!isActive}
            onClick={() => {
              handleClose();
              setStrategyOpen(true);
            }}
          >
            <SwapHorizIcon fontSize="small" />
            Switch to {nextStrategy}
          </StyledMenuItem>

          <StyledMenuItem
            onClick={() => {
              handleClose();
              setToggleOpen(true);
            }}
          >
            {isActive ? (
              <ToggleOffIcon fontSize="small" />
            ) : (
              <ToggleOnIcon fontSize="small" />
            )}
            {isActive ? "Deactivate" : "Activate"}
          </StyledMenuItem>

          <Divider sx={{ my: 0.5 }} />

          <StyledMenuItem
            onClick={() => {
              handleClose();
              setDeleteOpen(true);
            }}
            danger
          >
            <DeleteOutlineIcon fontSize="small" />
            Delete Profile
          </StyledMenuItem>
        </PopoverContent>
      </Popover>

      <CustomConfirmDialog
        open={deleteOpen}
        onClose={() => {
          if (deleteMutation.isPending) return;
          deleteMutation.reset();
          setDeleteOpen(false);
        }}
        title="Delete Profile"
        description="This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        mutation={deleteMutation}
        onConfirm={() => deleteMutation.mutate()}
      />

      <CustomConfirmDialog
        open={toggleOpen}
        onClose={() => {
          if (toggleMutation.isPending) return;
          toggleMutation.reset();
          setToggleOpen(false);
        }}
        title={`${isActive ? "Deactivate" : "Activate"} Profile`}
        description={`Are you sure you want to ${
          isActive ? "deactivate" : "activate"
        } this profile?`}
        confirmLabel={isActive ? "Deactivate" : "Activate"}
        variant={isActive ? "danger" : "warning"}
        mutation={toggleMutation}
        onConfirm={() => toggleMutation.mutate()}
      />

      <CustomConfirmDialog
        open={strategyOpen}
        onClose={() => {
          if (strategyMutation.isPending) return;
          strategyMutation.reset();
          setStrategyOpen(false);
        }}
        title="Change Selection Strategy"
        description={`This profile is currently using "${currentStrategy}". Do you want to switch to "${nextStrategy}"?`}
        confirmLabel={`Switch to ${nextStrategy}`}
        variant="warning"
        mutation={strategyMutation}
        onConfirm={() => strategyMutation.mutate()}
      />
      <UpdateRoutingProfileDialog
        open={updateOpen}
        onClose={() => setUpdateOpen(false)}
        profile={profile}
      />
      <RoutingProfileSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        profile={profile}
      />
    </>
  );
};

export default RouteProfilePopover;
